/* Harper & Stone — product page.
 *
 * Four small elements, none of which the page depends on: the form is a real
 * POST to /cart/add, the gallery is a scroller, the accordions are <details>,
 * and the recommendations band is empty until it fetches. With JS off the
 * page still sells.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

/* The gallery.
 *
 * Three jobs: keep the count honest as the carousel scrolls, move the
 * carousel when a thumbnail is tapped, and open the viewer when a photograph
 * is clicked. None of them is load-bearing — with JS off the track still
 * scrolls and the photographs are still there.
 */
class HsGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.pgal__track');
    if (!this.track) return;

    this.items = [...this.track.children];
    this.current = this.querySelector('[data-gallery-current]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    if (this.current) {
      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.#update());
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
    }

    for (const t of this.thumbs) {
      t.addEventListener('click', () => {
        const i = Number(t.dataset.galleryThumb);
        this.items[i]?.scrollIntoView({
          behavior: REDUCED.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      });
    }

    this.#viewer();

    this.onVariant = (e) => this.#showVariant(e.detail?.variant);
    document.addEventListener('variant:change', this.onVariant);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('variant:change', this.onVariant);
    cancelAnimationFrame(this.raf);
  }

  /* The gallery follows the variant picker.
   *
   * It did not. Choosing rose gold changed the price and the address bar and
   * left the yellow-gold photograph on screen: `variant:change` was
   * dispatched, and the only thing listening was the sticky bar. On a
   * jewellery page the metal *is* the decision, and the shopper made it and
   * watched nothing happen.
   *
   * A variant names its photograph by media id, which the items now carry.
   * What "show it" means depends on what the gallery is at this width:
   *
   *   below 48rem  a carousel — slide to the photograph. The track is
   *                scrolled directly rather than with scrollIntoView, which
   *                would also drag the page vertically.
   *   from 48rem   a column with one lead image — the variant's photograph
   *                takes the lead and the rest close up behind it, with the
   *                shared fade so the change reads as a change.
   */
  #showVariant(variant) {
    /* A variant with no photograph of its own leaves the gallery alone.
     *
     * The catalogue is photographed in yellow gold only, so white and rose
     * gold carry no media. Choosing one of them changes the price and the
     * address bar and keeps whatever photographs are on screen; choosing a
     * variant that does have a picture shows that picture. Nothing is
     * cleared, hidden or swapped for a blank. */
    const id = variant?.featured_media?.id;
    if (!id || !this.track) return;

    const item = this.track.querySelector(`[data-media-id="${id}"]`);
    if (!item) return;

    const carousel = !matchMedia('(min-width: 48rem)').matches;
    if (carousel) {
      this.track.scrollTo({
        left: item.offsetLeft - this.track.offsetLeft,
        behavior: REDUCED.matches ? 'auto' : 'smooth',
      });
      return;
    }

    if (item === this.track.firstElementChild) return;

    const promote = () => {
      this.track.querySelector('.pgal__item--lead')?.classList.remove('pgal__item--lead');
      item.classList.add('pgal__item--lead');
      this.track.prepend(item);

      /* The thumbnail strip is index-paired with the items, so it is
       * reordered the same way and both are renumbered — otherwise a later
       * resize down to the carousel would have thumb 1 opening image 3. */
      const strip = this.querySelector('.pgal__thumbs');
      const thumb = this.thumbs[Number(item.dataset.index) - 1];
      if (strip && thumb) strip.prepend(thumb);

      this.items = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      for (const [i, el] of this.items.entries()) el.dataset.index = String(i + 1);
      for (const [i, el] of this.thumbs.entries()) el.dataset.galleryThumb = String(i);
    };

    const swap = window.HS?.swap ?? ((_, change) => change());
    swap(this.track, promote);

    /* If the shopper has scrolled down the column, the lead is off screen
     * and the change they just asked for would happen where they cannot see
     * it. `scroll-padding` on the root keeps it clear of the pinned header. */
    if (this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  /* Which image is "current" is whichever one is nearest the middle of the
   * scroller — not scrollLeft / width, which lies on the last slide when the
   * track can't scroll a full item further. */
  #update() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (const [i, el] of this.items.entries()) {
      const centre = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(centre - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    if (this.current && this.current.textContent !== String(best + 1)) {
      this.current.textContent = String(best + 1);
    }
    for (const t of this.thumbs) {
      const on = Number(t.dataset.galleryThumb) === best;
      if (on) t.dataset.current = 'true';
      else delete t.dataset.current;
    }
  }

  /* The full-screen viewer. A native <dialog>, so focus trapping, Escape and
   * inerting the page behind it are the platform's problem, not ours.
   *
   * The sources live on the buttons rather than in a second array, so the
   * markup stays the single source of truth and the 2400px files are only
   * fetched when someone actually opens one. */
  #viewer() {
    this.dialog = this.querySelector('[data-viewer]');
    const openers = [...this.querySelectorAll('[data-viewer-open]')];
    if (!this.dialog || !openers.length) return;

    this.shots = openers.map((b) => ({ src: b.dataset.full, alt: b.dataset.alt || '' }));
    this.image = this.dialog.querySelector('[data-viewer-image]');
    this.viewerCurrent = this.dialog.querySelector('[data-viewer-current]');
    const total = this.dialog.querySelector('[data-viewer-total]');
    if (total) total.textContent = String(this.shots.length);

    // One image is not worth arrows.
    if (this.shots.length < 2) {
      for (const a of this.dialog.querySelectorAll('.pview__arrow, .pview__count')) {
        a.hidden = true;
      }
    }

    for (const b of openers) {
      b.addEventListener('click', () => this.#open(Number(b.dataset.viewerOpen)));
    }

    this.dialog.querySelector('[data-viewer-close]')?.addEventListener('click', () => {
      this.dialog.close();
    });
    this.dialog.querySelector('[data-viewer-prev]')?.addEventListener('click', () => this.#go(-1));
    this.dialog.querySelector('[data-viewer-next]')?.addEventListener('click', () => this.#go(1));

    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.#go(1);
      if (e.key === 'ArrowLeft') this.#go(-1);
    });

    /* Clicking the ground closes it. The stage is the whole middle column, so
     * the test is the image's own box: a click outside it is a click on the
     * ground, wherever in the dialog it landed. */
    this.dialog.addEventListener('click', (e) => {
      if (e.target.closest('button')) return;
      const r = this.image.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.dialog.close();
    });
  }

  #open(index) {
    this.index = index;
    this.#paint();
    this.dialog.showModal();
  }

  #go(by) {
    this.index = (this.index + by + this.shots.length) % this.shots.length;
    this.#paint();
  }

  #paint() {
    const shot = this.shots[this.index];
    if (!shot) return;
    this.image.dataset.loading = 'true';
    this.image.src = shot.src;
    this.image.alt = shot.alt;
    this.image.decode?.().catch(() => {}).finally(() => {
      delete this.image.dataset.loading;
    });
    if (this.viewerCurrent) this.viewerCurrent.textContent = String(this.index + 1);
  }
}

/* The variant picker.
 *
 * Resolves on the selected values in order, not on option names: the
 * catalogue calls the metal option "Color" on most rings and "Metal Color" on
 * the rest, so name-based logic breaks on a seventh of the range.
 */
class ProductForm extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.form = this.querySelector('.pform__form');
    this.idInput = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.qtyInput = this.querySelector('[data-qty-input]');

    const data = this.querySelector('[data-variant-data]');
    try {
      this.variants = data ? JSON.parse(data.textContent) : [];
    } catch {
      this.variants = [];
    }

    /* Variants indexed by their option values, built once.
     *
     * Every change used to scan the whole list comparing arrays — and the
     * availability pass below asks the same question once per option value,
     * so on a 42-variant pair of earrings that is a few hundred array
     * comparisons per click. Keyed by the joined values it is one Map read
     * per question. U+0001 cannot appear in an option value, so the key is
     * unambiguous where a space or a slash would not be. */
    this.byOptions = new Map(this.variants.map((v) => [v.options.join('\u0001'), v]));

    for (const input of this.querySelectorAll('[data-option-input]')) {
      input.addEventListener('change', () => this.#select());
    }

    this.#markAvailability();

    this.querySelector('[data-qty-down]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-qty-up]')?.addEventListener('click', () => this.#step(1));

    this.form?.addEventListener('submit', (e) => this.#submit(e));
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #step(by) {
    if (!this.qtyInput) return;
    const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + by);
    this.qtyInput.value = String(next);
  }

  /* The chosen values, in option order.
   *
   * An option renders as radios or as a <select> depending on how many values
   * it has, so this reads both. `:checked` never matches a select, and keying
   * on it alone silently dropped every carat choice. */
  get #selected() {
    return [...this.querySelectorAll('[data-option-input]')]
      .filter((el) => el.tagName === 'SELECT' || el.checked)
      .map((el) => el.value);
  }

  #select() {
    const chosen = this.#selected;
    const match = this.byOptions.get(chosen.join('\u0001'));
    this.#markAvailability();

    // Keep the legend's value in step with the choice.
    for (const [i, value] of chosen.entries()) {
      const label = this.querySelector(`[data-option-value="${i + 1}"]`);
      if (label) label.textContent = value;
    }

    if (!match) {
      this.#setButton(false, this.addButton?.dataset.unavailable || 'Unavailable');
      return;
    }

    if (this.idInput) this.idInput.value = match.id;
    this.#setButton(match.available);
    this.#publishPrice(match);

    // The line under the title names the metal in hand.
    const subtitle = document.querySelector('[data-variant-subtitle]');
    if (subtitle && match.option1) subtitle.textContent = match.option1;

    // Keep the address bar honest, so a shared link opens on the same variant.
    const url = new URL(location.href);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url);

    this.dispatchEvent(
      new CustomEvent('variant:change', { detail: { variant: match }, bubbles: true }),
    );
  }

  /* Show which choices can actually be bought.
   *
   * For each value of each option: keeping every *other* choice as it is,
   * would picking this value land on a variant that is in stock? If not, the
   * value is marked. Eleven products in the catalogue carry sold-out
   * variants, and until now the picker offered all of them identically — the
   * shopper found out by choosing one and watching the button change.
   *
   * Marked, not removed. A value that has vanished reads as "they do not
   * make it in platinum"; a value that is struck through reads as "not right
   * now", which is the truth. It stays selectable so the button can say
   * "Sold out" in so many words. A combination with no variant at all is
   * different — there is nothing to select — and is disabled outright. */
  #markAvailability() {
    const chosen = this.#selected;
    const inputs = [...this.querySelectorAll('[data-option-input]')];

    const stateOf = (index, value) => {
      const probe = chosen.slice();
      probe[index] = value;
      const v = this.byOptions.get(probe.join('\u0001'));
      if (!v) return 'missing';
      return v.available ? 'ok' : 'soldout';
    };

    /* Inputs appear in option order: every radio of option 1, then option 2…
     * A select is one input holding many values. The option index is the
     * position of the group, which the markup records on the wrapper. */
    for (const input of inputs) {
      const group = input.closest('[data-option-index]');
      const index = Number(group?.dataset.optionIndex) - 1;
      if (Number.isNaN(index)) continue;

      if (input.tagName === 'SELECT') {
        for (const option of input.options) {
          const state = stateOf(index, option.value);
          option.disabled = state === 'missing';
          const base = option.dataset.label ?? (option.dataset.label = option.textContent.trim());
          option.textContent = state === 'soldout' ? `${base} ${this.addButton?.dataset.soldOutSuffix || '(sold out)'}` : base;
        }
        /* The custom listbox mirrors the select; tell it the rows changed. */
        input.dispatchEvent(new CustomEvent('hs:options-changed', { bubbles: true }));
      } else {
        const state = stateOf(index, input.value);
        input.disabled = state === 'missing';
        if (state === 'ok') delete input.dataset.availability;
        else input.dataset.availability = state;
      }
    }
  }

  #setButton(available, label) {
    if (!this.addButton) return;
    this.addButton.disabled = !available;
    if (this.addLabel) {
      this.addLabel.textContent =
        label || (available ? this.addButton.dataset.add || 'Add to cart' : this.addButton.dataset.soldOut || 'Sold out');
    }
  }

  #publishPrice(variant) {
    const el = document.querySelector('.pdp__price');
    if (!el || !window.Shopify?.formatMoney) return;
    // The theme only reformats when Shopify's helper is present; otherwise the
    // server-rendered price stays, which is correct for the first variant.
    const money = window.Shopify.formatMoney(variant.price, window.HS?.moneyFormat);
    const node = [...el.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
    if (!node || node.textContent.trim() === money) return;
    /* A price that changes under the shopper should be seen to change. The
     * shared fade does it; an identical price is left alone so picking a
     * metal at the same price does not blink the number for no reason. */
    const swap = window.HS?.swap ?? ((_, change) => change());
    swap(el, () => {
      node.textContent = ` ${money} `;
    });
  }

  async #submit(e) {
    e.preventDefault();
    if (!this.form || this.addButton?.disabled) return;

    this.#error(null);
    this.addButton.setAttribute('aria-busy', 'true');

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const body = new FormData(this.form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
        signal: this.#controller.signal,
      });
      const json = await res.json();

      if (!res.ok) {
        this.#error(json.description || json.message || this.addButton?.dataset.addError || 'That could not be added.');
        return;
      }

      // /cart/add.js returns the line it created, `key` included — which is
      // what the drawer scrolls to. The drawer opening *is* the confirmation,
      // so the button no longer flashes "Added": two confirmations for one
      // action read as a stutter.
      document.dispatchEvent(new CustomEvent('cart:added', { detail: json, bubbles: true }));
    } catch (err) {
      if (err.name === 'AbortError') return;
      // A failed fetch must not strand the shopper: fall back to the real POST.
      this.form.submit();
    } finally {
      this.addButton.removeAttribute('aria-busy');
    }
  }

  #error(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }
}

/* The sticky bar appears once the real add-to-cart has scrolled away, and
 * hides again when it returns — so the two are never on screen together. */
class HsStickyBuy extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-add]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    const target = document.querySelector('.pform__submit');
    if (!target) return;

    this.button?.addEventListener('click', () => {
      document.querySelector('.pform__form')?.requestSubmit();
    });

    document.addEventListener('variant:change', (e) => {
      const el = document.querySelector('.pdp__price');
      if (this.priceEl && el) this.priceEl.textContent = el.textContent.trim();
      const available = e.detail?.variant?.available;
      if (this.button) this.button.disabled = !available;
    });

    /* Show the bar whenever the real button is off screen — in either
     * direction.
     *
     * This used to require `boundingClientRect.top < 0`, meaning the bar
     * appeared only once add-to-cart had scrolled *above* the viewport. On a
     * phone the button starts below the fold, so at first paint the shopper
     * had no price and no call to action anywhere on screen, and got one
     * only after scrolling past the thing they were looking for. A blind
     * critic named exactly this as the page's biggest gap against the
     * reference, which pins its bar from the start.
     *
     * Dropping the direction test means the bar is present immediately, then
     * steps aside while the real button is in view so the two are never on
     * screen together. */
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        this.hidden = !show;
        this.dataset.visible = String(show);
      },
      { threshold: 0 },
    );
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }
}

/* Recommendations arrive as rendered HTML from the recommendations endpoint,
 * so the card markup stays in Liquid and there is one card component. */
class HsProduct extends HTMLElement {
  connectedCallback() {
    this.#stickyOffsets();
    this.#recommendations();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  /* The panel pins under the header, so nothing needs measuring for the
   * offset itself. What does need measuring is whether the panel still fits:
   * on a short screen a panel with seven swatches and an open spec table is
   * taller than the space under the header, and a sticky box taller than its
   * travel never engages. `--pdp-panel-fits` is 1 when it fits and 0 when it
   * does not, and the CSS drops the sticky in the second case rather than
   * leaving a panel that looks pinned and is not. */
  #stickyOffsets() {
    const panel = this.querySelector('.pdp__panel');
    if (!panel) return;

    /* A panel taller than the screen still sticks — it just sticks to its
     * own bottom edge instead of its top.
     *
     * This used to switch sticky off whenever the panel did not fit, and
     * with seven swatches, a quantity stepper and two buttons it never fits:
     * the user reported the left column scrolling away while photographs
     * were still going past, which is exactly this branch firing.
     *
     * Pinning a too-tall box to the top is the thing that genuinely fails —
     * its lower half, including add-to-cart, is simply unreachable. Pinning
     * it to the *bottom* works: the panel scrolls with the page until its
     * last line reaches the foot of the screen, then holds there for the
     * rest of the gallery. The shopper sees the whole panel and it stays
     * with them, which is what the reference does.
     *
     * The offset is published as a negative top: `top: calc(100svh - h)`
     * expressed as a custom property the stylesheet consumes. */
    const measure = () => {
      const h = panel.getBoundingClientRect().height;
      const headerH =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        ) || 112;
      const room = window.innerHeight - headerH;
      const fits = h < room - 32;

      panel.style.setProperty('--pdp-panel-fits', fits ? '1' : '0');

      /* When it does not fit, pin the panel's bottom to the viewport's foot
       * with a little air, so the last control is never flush to the edge. */
      panel.style.setProperty(
        '--pdp-panel-top',
        fits ? '' : `${Math.round(window.innerHeight - h - 24)}px`,
      );
    };

    this.observer = new ResizeObserver(measure);
    this.observer.observe(panel);
    this.onResize = measure;
    window.addEventListener('resize', this.onResize, { passive: true });
    measure();
  }

  #recommendations() {
    const band = document.querySelector('[data-related]');
    if (!band?.dataset.url) return;

    const load = async () => {
      try {
        const res = await fetch(band.dataset.url);
        if (!res.ok) return;
        const html = await res.text();
        const next = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-related]');
        if (next && next.innerHTML.trim()) band.innerHTML = next.innerHTML;
      } catch {
        /* A missing rail is not worth an error; the page is complete without it. */
      }
    };

    // Only when it is nearly in view: this is the last band on the page and
    // most sessions never reach it.
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      },
      { rootMargin: '400px' },
    );
    io.observe(band);
  }
}

if (!customElements.get('hs-gallery')) customElements.define('hs-gallery', HsGallery);
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
if (!customElements.get('hs-sticky-buy')) customElements.define('hs-sticky-buy', HsStickyBuy);
if (!customElements.get('hs-product')) customElements.define('hs-product', HsProduct);

